import type { WHLocation } from '../../../types/warehouse'

export interface SelectedLocations {
  all: boolean
  warehouseIds: string[] // ignored when `all` is true
}

export interface ApiLocationRole {
  enabled: boolean
  location_id: string
  warehouse_id: string
}

// Build a serializer-valid `location_roles` payload from the dialog state +
// the warehouse list provided by PantryProvider.
//
// The backend `LocationRoleSerializer` (in
// `crm_be_v2_api/serializers/organization.py`) requires
// `{ enabled, location_id, warehouse_id }` — all three.
//
// Shape contract (confirmed via working invite curls + legacy
// `frontend/src/pages/Settings/Users/List/InviteDialog/index.js`):
//
//   • "All locations" selected → emit `[]`. The parent InvitedStoreSerializer
//     treats an empty array as "no per-location restriction".
//
//   • Partial selection → emit EVERY linked warehouse, each with its own
//     `enabled: true|false` flag reflecting whether the user picked it.
//     The legacy seeds `locationsRole` with one entry per warehouse
//     (`enabled: true` by default) and the submit transforms that map into
//     an array preserving all entries' enabled flags. Confirmed example:
//       [{ enabled:true,  location_id:226534, warehouse_id:"…" },
//        { enabled:false, location_id:239539, warehouse_id:"…" }]
//     A prior version of this function only emitted the *selected*
//     warehouses with `enabled:true`; that drops the "explicitly denied"
//     locations the backend expects to see.
//
// `location_id` ← `wh.id` (the POS-derived id from
// `cova_metadata.LocationId` / `greenline_mapping.location_id` /
// `pos_mapping.location_id`, stringified by warehouseApis). The backend
// coerces string ↔ int on its side.
export function buildLocationRoles(
  selected: SelectedLocations,
  warehouses: WHLocation[],
): ApiLocationRole[] {
  if (selected.all) return []
  return warehouses.map((wh) => ({
    enabled: selected.warehouseIds.includes(wh.warehouse_id),
    location_id: wh.id ?? '',
    warehouse_id: wh.warehouse_id,
  }))
}
